import React, { useEffect, useState } from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import media from '../styles/media'
import Colors from '../styles/Colors'
import { TextLink } from './Buttons'
import { Overline, Subtitle2 } from './Text'
import { StrikeThroughStyles } from './Text'
import { ReactComponent as LogoSVG } from 'svg/LogoMLight.svg'
import { ReactComponent as LogoDarkSVG } from 'svg/LogoMDark.svg'
import { ReactComponent as TwitterSVG } from 'svg/twitterBlack.svg'
import { ReactComponent as VimeoSVG } from 'svg/vimeoBlack.svg'
import { ReactComponent as YouTubeSVG } from 'svg/youTubeBlack.svg'
import { ReactComponent as LinkedInSVG } from 'svg/linkedInBlack.svg'
import { ReactComponent as TwitterDarkSVG } from 'svg/twitterDarkMode.svg'
import { ReactComponent as VimeoDarkSVG } from 'svg/vimeoDarkMode.svg'
import { ReactComponent as YouTubeDarkSVG } from 'svg/youtubeDarkMode.svg'
import { ReactComponent as LinkedInDarkSVG } from 'svg/linkedinDarkMode.svg'
import { ReactComponent as ArrowSVG } from 'svg/arrowSmall.svg'

type Props = {
  isDarkMode?: boolean
}

const Footer: React.FC<Props> = ({ isDarkMode }) => {
  const [thisYear, setThisYear] = useState(2021)

  useEffect(() => {
    const date = new Date()

    const year = date.getFullYear()

    setThisYear(year)
  }, [])

  return (
    <Wrapper isDarkMode={isDarkMode}>
      {!isDarkMode ? (
        <Link to="/" aria-label="Home">
          <Logo />
        </Link>
      ) : (
        <Link to="/" aria-label="Home">
          <LogoDark />
        </Link>
      )}
      <TopRow>
        <Links>
          <List isDarkMode={isDarkMode}>
            <li>
              <Link to="/products">Product</Link>
            </li>
            <li>
              <Link to="/resources">Resources</Link>
            </li>
            <li>
              <Link to="/support">Support</Link>
            </li>
            <li>
              <PrivacyPolicy to="/privacy-policy">Privacy Policy</PrivacyPolicy>
            </li>
          </List>
          <List isDarkMode={isDarkMode}>
            <li>
              <Link to="/about">About</Link>
            </li>
            <li>
              <a
                href="https://jobs.rtr.ai/ax.do?refresh=true&portalGK=35055"
                rel="noopener noreferrer"
                target="__blank"
              >
                Current Openings
              </a>
            </li>
            <li>
              <Link to="/resources" state={{ type: 'News' }}>
                News
              </Link>
            </li>
            <li>
              <Link to="/products/#contact">Contact</Link>
            </li>
          </List>
        </Links>
        <Email
          action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8"
          method="POST"
        >
          <input
            type="hidden"
            name="captcha_settings"
            value='{"keyname":"RTRnew2021v2","fallback":"true","orgId":"00D1U000000svSI","ts":""}'
          />
          <input type="hidden" name="oid" value="00D1U000000svSI" />
          <input type="hidden" name="retURL" value="https://rtr.ai" />
          <input name="lead_source" type="hidden" value="Website" />
          <label htmlFor="email">
            <Label isDarkMode={isDarkMode}>Sign up for our newsletter</Label>
          </label>
          <Row>
            <EmailInput
              placeholder="Email"
              type="email"
              required
              aria-required
              id="email"
              name="email"
              isDarkMode={isDarkMode}
            />
            <Send isDarkMode={isDarkMode} aria-label="send" type="submit">
              <Arrow />
            </Send>
          </Row>
        </Email>
      </TopRow>
      <BottomRow isDarkMode={isDarkMode}>
        <Socials>
          <a
            href="https://www.linkedin.com/company/realtimerobotics"
            aria-label="Linkedin"
            target="_blank"
            rel="noopener noreferrer"
          >
            {' '}
            {isDarkMode ? <LinkedInDarkMode /> : <LinkedIn />}
          </a>
          <a
            href="https://twitter.com/realtime_i?lang=en"
            aria-label="Twitter"
            target="_blank"
            rel="noopener noreferrer"
          >
            {' '}
            {isDarkMode ? <TwitterDarkMode /> : <Twitter />}
          </a>
          <a
            href="https://vimeo.com/realtimerobotics"
            aria-label="Vimeo"
            target="_blank"
            rel="noopener noreferrer"
          >
            {' '}
            {isDarkMode ? <VimeoDarkMode /> : <Vimeo />}
          </a>
          <a
            href="https://www.youtube.com/channel/UCO9TmqYHnnyIJU5agg0Trkg"
            aria-label="Youtube"
            target="_blank"
            rel="noopener noreferrer"
          >
            {isDarkMode ? <YouTubeDarkMode /> : <YouTube />}
          </a>
        </Socials>

        <FooterLink1
          style={{
            color: isDarkMode ? Colors.whiteSmoke : Colors.softBlack,
          }}
          to="/privacy-policy"
        >
          Privacy Policy
        </FooterLink1>

        <Divider isDarkMode={isDarkMode}>|</Divider>
        <CopyRight isDarkMode={isDarkMode}>
          © {thisYear} Realtime Robotics, Inc. • All Rights Reserved
        </CopyRight>
      </BottomRow>
    </Wrapper>
  )
}

const Wrapper = styled.footer<{ isDarkMode: boolean }>`
  width: 100%;
  height: 820px;
  position: relative;
  background: ${(props) =>
    props.isDarkMode ? Colors.softBlack : Colors.whiteSmoke};
  padding: 164px 120px 96px;
  overflow: hidden;
  ${media.tabletLandscape} {
    padding: 96px 0 48px 72px;
    height: 621px;
  }

  ${media.tablet} {
    padding: 96px 0 48px 48px;
  }
  ${media.mobile} {
    height: 712px;
    padding: 64px 0;
  }
`

const TopRow = styled.div`
  display: flex;
  width: 960px;
  position: absolute;
  right: 120px;
  ${media.tabletLandscape} {
    left: 7vw;
    right: auto;
  }
  ${media.mobile} {
    left: 12px;
  }
`
const List = styled.ul<{ isDarkMode: boolean }>`
  list-style-type: none;
  margin: 0;
  li {
    padding-bottom: 24px;
  }
  a {
    text-decoration: none;
    color: ${(props) =>
      props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};

    font-size: 16px;
    line-height: 16px;
    height: 16px;
    font-weight: 500;
    ${StrikeThroughStyles}
    :hover {
      color: ${(props) =>
        props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};
      opacity: 0.48;
      transition: 0.8s;
      transition-timing-function: cubic-bezier(0.72, 0, 0.12, 1);
    }
    :after {
      top: calc(50%);
    }
  }
  ${media.tabletLandscape} {
    a {
      font-size: 24px;
      line-height: 36px;
    }
  }
  ${media.mobile} {
    a {
      line-height: 36px;
    }
  }
`

const Links = styled.div`
  width: 376px;
  margin-right: 238px;
  display: flex;
  justify-content: space-between;
  ${media.tabletLandscape} {
    width: 525px;
  }
  ${media.tablet} {
    width: 447px;
  }
  ${media.mobile} {
    flex-direction: column-reverse;
  }
`

const PrivacyPolicy = styled(Link)`
  display: none;
  ${media.tabletLandscape} {
    display: inline;
  }
`
const Logo = styled(LogoSVG)`
  position: absolute;
  width: 48px;
  @media screen and (max-width: 1300px) {
    display: none;
  }
`
const LogoDark = styled(LogoDarkSVG)`
  position: absolute;
  width: 48px;
  @media screen and (max-width: 1300px) {
    display: none;
  }
`

const Socials = styled.div`
  display: flex;
  align-items: center;
  a {
    :hover {
      opacity: 0.45;
    }
  }
  ${media.tablet} {
  }
`
const BottomRow = styled.div<{ isDarkMode: boolean }>`
  position: absolute;
  right: 96px;
  padding-right: 24px;
  bottom: 140px;
  display: flex;
  align-items: center;
  :after {
    content: ' ';
    width: 100%;
    height: 1px;
    background: ${(props) =>
      props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};

    position: absolute;
    left: 0;
    top: 72px;
  }
  ${media.tabletLandscape} {
    width: 88.3vw;
    left: 7vw;
    right: auto;
    justify-content: space-between;
    bottom: 57px;
  }
  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    width: calc(100% - 24px);
    left: 12px;
    padding: 0;
  }
`

const Divider = styled.span<{ isDarkMode: boolean }>`
  margin-left: 24px;
  margin-right: 24px;
  color: ${(props) =>
    props.isDarkMode ? ` ${Colors.whiteSmoke}` : ` ${Colors.softBlack}`};
  ${media.tabletLandscape} {
    display: none;
  }
`

const Twitter = styled(TwitterSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const Vimeo = styled(VimeoSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const LinkedIn = styled(LinkedInSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const YouTube = styled(YouTubeSVG)`
  width: 24px;
  height: 24px;

  ${media.tablet} {
  }
`
const TwitterDarkMode = styled(TwitterDarkSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const VimeoDarkMode = styled(VimeoDarkSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const LinkedInDarkMode = styled(LinkedInDarkSVG)`
  width: 24px;
  height: 24px;
  margin-right: 48px;
  ${media.tablet} {
  }
`
const YouTubeDarkMode = styled(YouTubeDarkSVG)`
  width: 24px;
  height: 24px;

  ${media.tablet} {
  }
`

const FooterLink1 = styled(TextLink)`
  position: relative;
  margin-left: 166px;
  font-size: 16px;
  line-height: 16px;
  height: 16px;
  opacity: 1;
  :hover {
    opacity: 0.48;
    transition: 0.8s;
    transition-timing-function: cubic-bezier(0.72, 0, 0.12, 1);
  }
  ${media.tabletLandscape} {
    display: none;
  }
`

const CopyRight = styled(Overline)<{ isDarkMode: boolean }>`
  font-weight: 400;
  color: ${(props) =>
    props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};
  ${media.tabletLandscape} {
  }
  ${media.mobile} {
    font-weight: 700;
    font-size: 12px;
    line-height: 12px;
    text-transform: none;
    letter-spacing: 0.5px;
    margin-top: 21px;
  }
`

const Label = styled(Subtitle2)<{ isDarkMode: boolean }>`
  color: ${(props) =>
    props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};
  margin-bottom: 12px;
  ${media.tablet} {
  }
`

const Email = styled.form`
  display: flex;
  flex-direction: column;

  ${media.tabletLandscape} {
    display: none;
  }
`

const Row = styled.div`
  display: flex;
  flex-direction: row;

  ${media.tablet} {
  }
`

const EmailInput = styled.input<{ isDarkMode: boolean }>`
  height: 48px;
  width: 346px;
  background: ${Colors.neptune12};
  border: ${(props) =>
    props.isDarkMode
      ? `solid 1px ${Colors.whiteSmoke}`
      : `solid 1px ${Colors.softBlack}`};

  padding-left: 24px;
  font-family: 'Maison Neue';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  outline: none;
  font-family: 'Maison Neue Mono';

  color: ${(props) =>
    props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};
  ${media.tablet} {
  }
`

const Send = styled.button<{ isDarkMode: boolean }>`
  width: 48px;
  height: 48px;
  background: none;
  border: ${(props) =>
    props.isDarkMode
      ? `1px solid ${Colors.whiteSmoke}`
      : `1px solid ${Colors.softBlack}`};
  color: ${(props) =>
    props.isDarkMode ? ` ${Colors.whiteSmoke}` : ` ${Colors.softBlack}`};
  border-left: 0px solid white;
  font-family: 'Maison Neue';
  font-style: normal;
  font-weight: 500;
  font-size: 1.4vw;
  line-height: 117%;
  ${media.tablet} {
  }
`

const Arrow = styled(ArrowSVG)<{ isDarkMode: boolean }>`
  width: 1.3vw;
  color: inherit;
  ${media.tablet} {
  }
`

export default Footer
